/**
 * Routing and the authentication guard.
 *
 * Two rules the guard enforces, both of which have to hold on a cold load and
 * not only on in-app navigation:
 *
 *  1. A signed-out user reaching a protected route is sent to `/login` with the
 *     destination preserved, so signing in lands them where they were going.
 *  2. A route declaring a permission is unreachable without it. The server
 *     re-checks (this is not the boundary), but a 403 page is a worse answer
 *     than never offering the link.
 */

import { AUTH_SSO_ROUTES } from '@/modules/auth/routes'
import {
  createRouter,
  createWebHistory,
  START_LOCATION,
  type RouteRecordRaw,
  type Router,
} from 'vue-router'
import { watch } from 'vue'

import { passwordChangeRequired, sessionExpired } from '@/api/client'
import { t } from '@/i18n'
import { PERMISSIONS } from '@/lib/permissions'
import { AI_ROUTES } from '@/modules/ai/routes'
import { AP_PERMISSIONS } from '@/modules/ap/permissions'
import { AP_C3_ROUTES } from '@/modules/ap/routesC3'
import { AP_SETTINGS_ROUTES } from '@/modules/ap/settings/routes'
import { AP_DUPLICATE_PAYMENTS_ROUTES } from '@/modules/ap/reports/duplicate-payments/routes'
import { AP_STATEMENT_RECONCILIATION_ROUTES } from '@/modules/ap/reports/statement-reconciliation/routes'
import { AP_SUPPLIER_STATEMENT_ROUTES } from '@/modules/ap/reports/supplier-statement/routes'
import { AP_WITHHOLDING_ROUTES } from '@/modules/ap/reports/withholding/routes'
import { ASSETS_ROUTES } from '@/modules/assets/routes'
import { ATTENTION_ROUTES } from '@/modules/attention/routes'
import { NOTIFICATIONS_ROUTES } from '@/modules/notifications/routes'
import { AUTHORITY_ROUTES } from '@/modules/authority/routes'
import { BANKREC_C4_ROUTES, BANKREC_ROUTES } from '@/modules/bankrec/routes'
import { BUDGET_ROUTES } from '@/modules/budget/routes'
import { CLOSE_CERTIFICATIONS_ROUTES } from '@/modules/close/certifications/routes'
import { CLOSE_CYCLE_ANALYSIS_ROUTES } from '@/modules/close/cycle-analysis/routes'
import { CLOSE_EVIDENCE_PACKS_ROUTES } from '@/modules/close/evidence-packs/routes'
import { CLOSE_RECONCILIATION_ROUTES } from '@/modules/close/reconciliation/routes'
import { CLOSE_ROUTES } from '@/modules/close/routes'
import { CLOSE_STATEMENTS_ROUTES } from '@/modules/close/statements/routes'
import { CLOSE_YEAR_END_ROUTES } from '@/modules/close/year-end/routes'
import { CONSOL_ROUTES } from '@/modules/consol/routes'
import { COST_ROUTES } from '@/modules/cost/routes'
import { CRM_C5_ROUTES, CRM_ROUTES } from '@/modules/crm/routes'
import { EAM_ROUTES } from '@/modules/eam/routes'
import { ESS_ROUTES } from '@/modules/ess/routes'
import { ESS_C10_ROUTES } from '@/modules/ess/routesC10'
import { EXPENSE_ROUTES } from '@/modules/expense/routes'
import { HR_ROUTES } from '@/modules/hr/routes'
import { HR_C10_ROUTES } from '@/modules/hr/routesC10'
import { INTERCO_ROUTES } from '@/modules/interco/routes'
import { INVENTORY_ROUTES } from '@/modules/inventory/routes'
import { MFG_ROUTES } from '@/modules/mfg/routes'
import { OPCAL_ROUTES } from '@/modules/opcal/routes'
import { PAYROLL_ROUTES } from '@/modules/payroll/routes'
import { PAYROLL_C10_ROUTES } from '@/modules/payroll/routesC10'
import { PMS_ROUTES } from '@/modules/pms/routes'
import { ROSTER_ROUTES } from '@/modules/roster/routes'
import { HOUSEKEEPING_ROUTES as PMS_HOUSEKEEPING_ROUTES } from '@/modules/pms/housekeeping/routes'
import { PMS_OPS_ROUTES } from '@/modules/pms/ops/routes'
import { POS_ROUTES } from '@/modules/pos/routes'
import { PRICING_ROUTES } from '@/modules/pricing/routes'
import { PROCUREMENT_ROUTES } from '@/modules/procurement/routes'
import { PROJECTS_ROUTES } from '@/modules/projects/routes'
import { QMS_ROUTES } from '@/modules/qms/routes'
import { REPORTING_ROUTES } from '@/modules/reporting/routes'
import { RULES_ROUTES } from '@/modules/rules/routes'
import { RVM_ROUTES } from '@/modules/rvm/routes'
import { SALES_ROUTES } from '@/modules/sales/routes'
import { SERVICE_ROUTES } from '@/modules/service/routes'
import { TAX_SPINE_ROUTES } from '@/modules/tax/routes'
import { AR_ROUTES } from '@/modules/ar/routes'
import { CASH_PERMISSIONS } from '@/modules/cash/permissions'
import { FX_C1_ROUTES } from '@/modules/fx/routesC1'
import { GL_GOVERNANCE_ROUTES } from '@/modules/gl/routes'
import { GL_PERMISSIONS } from '@/modules/gl/permissions'
import { ORG_C1_ROUTES } from '@/modules/org/routesC1'
import { ORG_PERMISSIONS } from '@/modules/org/permissions'
import { CASH_TRANSFER_ROUTES } from '@/modules/cash/transfers/routes'
import { TREASURY_CONCENTRATION_ROUTES } from '@/modules/treasury/concentration/routes'
import { TREASURY_ROUTES } from '@/modules/treasury/routes'
import { TREASURY_WALLET_COUNTS_ROUTES } from '@/modules/treasury/wallet-counts/routes'
import { TREASURY_WALLET_EXPENDITURE_ROUTES } from '@/modules/treasury/wallet-expenditure/routes'
import { TREASURY_WALLET_FUNDING_ROUTES } from '@/modules/treasury/wallet-funding/routes'
import { TREASURY_BANK_CONNECTION_ROUTES } from '@/modules/treasury/bank-connections/routes'
import { TREASURY_ACCRUAL_RUNS_ROUTES } from '@/modules/treasury/debt/accruals/routes'
import { TREASURY_LIQUIDITY_GAP_ROUTES } from '@/modules/treasury/liquidity-gap/routes'
import { TREASURY_LIQUIDITY_ROUTES } from '@/modules/treasury/liquidity/routes'
import { TREASURY_POSITION_GROUP_ROUTES } from '@/modules/treasury/positions/routes'
import { BANKREC_IMPORT_LINES_ROUTES } from '@/modules/bankrec/imports/routes'
import { AR_ECL_SETTINGS_ROUTES } from '@/modules/ar/ecl/routes'
import { AR_COLLECTIONS_ROUTES } from '@/modules/ar/collections/routes'
import { AR_WRITE_OFF_ROUTES } from '@/modules/ar/write-offs/routes'
import { AR_CREDIT_CONTROL_ROUTES } from '@/modules/ar/credit-control/routes'
import { AR_CEI_ROUTES } from '@/modules/ar/reports/cei/routes'
import { AR_UNALLOCATED_RECEIPTS_ROUTES } from '@/modules/ar/reports/unallocated-receipts/routes'
import { AR_DUNNING_EFFECTIVENESS_ROUTES } from '@/modules/ar/reports/dunning-effectiveness/routes'
import { TREASURY_INTERCOMPANY_ROUTES } from '@/modules/treasury/intercompany/routes'
import { TREASURY_SCREENING_ROUTES } from '@/modules/treasury/screening/routes'
import { TREASURY_RECONCILIATION_EXCEPTIONS_ROUTES } from '@/modules/treasury/reconciliation-exceptions/routes'
import { COST_QUANTITIES_ROUTES } from '@/modules/cost/quantities/routes'
import { COST_REPORT_ROUTES } from '@/modules/cost/reports/routes'
import { EXPENSE_CARD_ACCOUNT_ROUTES } from '@/modules/expense/card-accounts/routes'
import { EXPENSE_SETTINGS_ROUTES } from '@/modules/expense/settings/routes'
import { PARTY_ROUTES } from '@/modules/party/routes'
// C14 W3 (FSD Part 1 "Enterprise", 2026-09-19): one route array per feature
// spec, each owning its own paths/permissions/titles exactly like every other
// module above. C17 removed the two pre-FSD screens these superseded (companies,
// divisions); their old paths redirect here via legacyRedirects.ts.
import { ENTERPRISE_SETUP_ROUTES } from '@/modules/enterprise/setup/routes'
import { DIVISION_ROUTES } from '@/modules/enterprise/divisions/routes'
import { LEGAL_ENTITY_ROUTES } from '@/modules/enterprise/legal-entities/routes'
import { BUSINESS_UNIT_ROUTES } from '@/modules/enterprise/business-units/routes'
import { WORKFORCE_ROUTES } from '@/modules/enterprise/workforce/routes'
import { REFERENCE_SET_ROUTES } from '@/modules/enterprise/reference-sets/routes'
import { FINANCIAL_STRUCTURES_ROUTES } from '@/modules/enterprise/financial-structures/routes'
import { GOVERNANCE_ROUTES } from '@/modules/enterprise/governance/routes'
import { LOCALISATION_ROUTES } from '@/modules/enterprise/localisation/routes'
import { HIERARCHY_ROUTES } from '@/modules/enterprise/hierarchies/routes'
// FSD Part 1 menu parity (R0): the reference menu's remaining screens, each its
// own route table. Three of them replace legacy screens outright — period
// control (`/settings/organization/fiscal-periods`), currency rates
// (`/fx/rates`) and the dashboard (`/settings/organization/go-live`); the old
// paths are redirects in legacyRedirects.ts, not routes.
import { ENTERPRISE_OVERVIEW_ROUTES } from '@/modules/enterprise/overview/routes'
import { ENTERPRISE_SIMULATOR_ROUTES } from '@/modules/enterprise/simulator/routes'
import { ENTERPRISE_GEOGRAPHY_ROUTES } from '@/modules/enterprise/geographies/routes'
import { ENTERPRISE_LEDGER_ROUTES } from '@/modules/enterprise/ledgers/routes'
import { ENTERPRISE_PERIOD_ROUTES } from '@/modules/enterprise/periods/routes'
import { ENTERPRISE_CURRENCY_RATE_ROUTES } from '@/modules/enterprise/currency-rates/routes'
import { LEGACY_ORGANIZATION_REDIRECTS } from '@/router/legacyRedirects'
import { useAuthStore } from '@/stores/auth'

declare module 'vue-router' {
  interface RouteMeta {
    /** Reachable without a session (login, password reset, invite). */
    public?: boolean
    /** Permission code required to enter. */
    permission?: string
    /**
     * Message key for the breadcrumb and the document title. A key rather
     * than a string because the title is resolved on every navigation, which
     * is exactly when a locale switch has to be picked up.
     */
    titleKey?: string
    /** Renders outside the app shell (auth screens). */
    bare?: boolean
  }
}

// Exported so a test can assert every titleKey it names actually exists. vue-i18n
// renders a missing key as the key itself, so the failure ships as a dotted path in the
// breadcrumb rather than as an error — see src/nav/keys.test.ts.
export const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/modules/auth/LoginView.vue'),
    meta: { public: true, bare: true, titleKey: 'routes.titles.login' },
  },
  {
    path: '/forgot-password',
    name: 'forgot-password',
    component: () => import('@/modules/auth/ForgotPasswordView.vue'),
    meta: { public: true, bare: true, titleKey: 'routes.titles.forgotPassword' },
  },
  {
    path: '/reset-password',
    name: 'reset-password',
    component: () => import('@/modules/auth/ResetPasswordView.vue'),
    meta: { public: true, bare: true, titleKey: 'routes.titles.resetPassword' },
  },
  {
    path: '/accept-invite',
    name: 'accept-invite',
    component: () => import('@/modules/auth/AcceptInviteView.vue'),
    meta: { public: true, bare: true, titleKey: 'routes.titles.acceptInvite' },
  },
  // OIDC sign-in lands here with a one-time ticket (identity/sso_service.go).
  ...AUTH_SSO_ROUTES,
  // The forced password change. `bare`, like its neighbours above, but NOT
  // `public`: it needs a session, and the endpoint behind it authenticates. It
  // sits here rather than under /profile because it is not a settings screen —
  // while the server is refusing every other tenant route
  // (`httpapi.RequireOwnPassword`), it is the entire application.
  {
    path: '/change-password',
    name: 'change-password',
    component: () => import('@/modules/auth/ChangePasswordView.vue'),
    meta: { bare: true, titleKey: 'routes.titles.changePassword' },
  },

  // The home screen: every app as a tile, the shared daily-work screens, the
  // user's pins and recents, and — first — their role's dashboard. The route
  // keeps its `dashboard` name because sign-in, invite acceptance and the
  // guard all navigate to it by that name; only what renders here changed.
  {
    path: '/',
    name: 'dashboard',
    component: () => import('@/modules/home/HomeView.vue'),
    meta: { titleKey: 'routes.titles.dashboard' },
  },
  {
    path: '/approvals',
    name: 'approvals',
    component: () => import('@/modules/approvals/ApprovalsInboxView.vue'),
    meta: { titleKey: 'routes.titles.approvals', permission: PERMISSIONS.approvalAct },
  },

  // Old paths whose screen C17 / R0 removed (companies, divisions, fiscal
  // periods, go-live, FX rates, naming templates, headcount), redirected to the
  // FSD screen that replaced them. Spread HERE, ahead of every other
  // organisation record, on purpose: vue-router 4 inserts a matcher of equal
  // score AFTER the ones already registered and `resolve` takes the first
  // match, so a redirect declared after a record with the same path is dead.
  // That is exactly what happened from C14 W3 until C17 — this spread sat
  // below the legacy records, and all four of its redirects silently lost to
  // the screens they were meant to replace. Being first is necessary but not
  // sufficient: no record anywhere in this table may share a path with an
  // entry in LEGACY_ORGANIZATION_REDIRECTS (`legacyRedirects.test.ts`
  // asserts it on the real table).
  ...LEGACY_ORGANIZATION_REDIRECTS,

  // Organization (§8.1). The paths match the deep links the server puts on
  // go-live checklist items and in briefing emails — those are a contract, not a
  // convenience, so they are not renamed here (a removed screen's path becomes
  // a redirect, never a 404). Hierarchy (branches, departments,
  // cost/profit centres) and accounts (GL accounts, COA templates) are kept live
  // in C17 as the FSD 1.2.5 / 1.2.7 menu items `org-units` and
  // `org-chart-of-accounts` (DECISIONS.md C17); they are the only screens that
  // maintain those records.
  {
    path: '/settings/organization/hierarchy',
    name: 'org-hierarchy',
    component: () => import('@/modules/org/HierarchyView.vue'),
    meta: { titleKey: 'routes.titles.orgHierarchy', permission: ORG_PERMISSIONS.unitRead },
  },
  {
    path: '/settings/organization/accounts',
    name: 'org-accounts',
    component: () => import('@/modules/org/AccountsView.vue'),
    meta: { titleKey: 'routes.titles.orgAccounts', permission: ORG_PERMISSIONS.accountRead },
  },
  {
    path: '/settings/organization/opening-balances',
    name: 'org-opening-balances',
    component: () => import('@/modules/org/OpeningBalancesView.vue'),
    meta: {
      titleKey: 'routes.titles.orgOpeningBalances',
      permission: ORG_PERMISSIONS.openingBalanceRead,
    },
  },

  // C1 organization deltas (design §5.9): locations, jobs, grades, positions,
  // designations and the filing calendar (C17 removed the divisions screen —
  // its path redirects to FSD 1.2.2 above). Spread whole, like every other
  // module's own route table — the module owns its paths, permissions and
  // title keys.
  ...ORG_C1_ROUTES,

  // C14 W3 (FSD Part 1 "Enterprise") — nine feature specs, nine route arrays,
  // each spread whole like every table above. `nav/parts/p01-enterprise.ts`
  // points here. The two pre-FSD screens these replaced (companies, divisions)
  // were removed in C17; their old paths are redirects (spread near the top of
  // this table) rather than 404s, because CLAUDE.md §0.1's "no stub" rule cuts
  // both ways: a route the server has already sent to a user in a briefing
  // email does not get to become a dead link.
  ...ENTERPRISE_SETUP_ROUTES,
  ...DIVISION_ROUTES,
  ...LEGAL_ENTITY_ROUTES,
  ...BUSINESS_UNIT_ROUTES,
  ...WORKFORCE_ROUTES,
  ...REFERENCE_SET_ROUTES,
  ...FINANCIAL_STRUCTURES_ROUTES,
  ...GOVERNANCE_ROUTES,
  ...LOCALISATION_ROUTES,
  ...HIERARCHY_ROUTES,
  ...ENTERPRISE_OVERVIEW_ROUTES,
  ...ENTERPRISE_SIMULATOR_ROUTES,
  ...ENTERPRISE_GEOGRAPHY_ROUTES,
  ...ENTERPRISE_LEDGER_ROUTES,
  ...ENTERPRISE_PERIOD_ROUTES,
  ...ENTERPRISE_CURRENCY_RATE_ROUTES,

  // The operating calendar (design §5.9, §7). Config screens ride here beside
  // the rest of organization setup; `/operations/day-board` is in this table
  // too even though it is worked daily, not configured — opcal/routes.ts
  // explains the split.
  ...OPCAL_ROUTES,

  // The enterprise authority matrix (design §4, §5.9): who may approve what,
  // delegation, absence reassignment and materiality thresholds. Its own
  // top-level `/authority/*` prefix, not nested under settings — approving is
  // a job people do, not a configuration screen.
  ...AUTHORITY_ROUTES,

  // The control-rule / reason-code / override / reconciliation-queue
  // framework (design §8, §5.9), mounted under `/controls` rather than
  // `/settings` — see rules/routes.ts.
  ...RULES_ROUTES,

  // The unified attention list (design §10, §5.9). Top-level `/attention`,
  // not nested under any one module: it is the projection over approvals,
  // exceptions, tasks and alerts from all of them.
  ...ATTENTION_ROUTES,

  // CLAUDE.md §6: the notification inbox behind the command bar's bell. Top
  // level for the same reason `/attention` is — it is nobody's module screen,
  // and it is read rather than configured.
  ...NOTIFICATIONS_ROUTES,

  // General ledger (§8.2). `/gl/journal-entries/new` is declared before the
  // `:id` route so `new` is never parsed as an entry id.
  {
    path: '/gl/journal-entries',
    name: 'gl-journal-entries',
    component: () => import('@/modules/gl/JournalEntriesListView.vue'),
    meta: { titleKey: 'routes.titles.glJournalEntries', permission: GL_PERMISSIONS.journalRead },
  },
  {
    path: '/gl/journal-entries/new',
    name: 'gl-journal-entry-new',
    component: () => import('@/modules/gl/JournalEntryView.vue'),
    meta: { titleKey: 'routes.titles.glJournalEntryNew', permission: GL_PERMISSIONS.journalCreate },
  },
  {
    path: '/gl/journal-entries/:id(\\d+)',
    name: 'gl-journal-entry',
    component: () => import('@/modules/gl/JournalEntryView.vue'),
    meta: { titleKey: 'routes.titles.glJournalEntry', permission: GL_PERMISSIONS.journalRead },
  },
  {
    path: '/gl/reports/trial-balance',
    name: 'gl-trial-balance',
    component: () => import('@/modules/gl/TrialBalanceView.vue'),
    meta: { titleKey: 'routes.titles.glTrialBalance', permission: GL_PERMISSIONS.reportRead },
  },
  {
    path: '/gl/reports/income-statement',
    name: 'gl-income-statement',
    component: () => import('@/modules/gl/StatementView.vue'),
    props: { kind: 'income_statement' },
    meta: { titleKey: 'routes.titles.glIncomeStatement', permission: GL_PERMISSIONS.reportRead },
  },
  {
    path: '/gl/reports/balance-sheet',
    name: 'gl-balance-sheet',
    component: () => import('@/modules/gl/StatementView.vue'),
    props: { kind: 'balance_sheet' },
    meta: { titleKey: 'routes.titles.glBalanceSheet', permission: GL_PERMISSIONS.reportRead },
  },
  {
    path: '/gl/reports/ledger',
    name: 'gl-ledger',
    component: () => import('@/modules/gl/LedgerView.vue'),
    meta: { titleKey: 'routes.titles.glLedger', permission: GL_PERMISSIONS.reportRead },
  },
  {
    path: '/gl/recurring-templates',
    name: 'gl-recurring-templates',
    component: () => import('@/modules/gl/RecurringTemplatesView.vue'),
    meta: {
      titleKey: 'routes.titles.glRecurringTemplates',
      permission: GL_PERMISSIONS.recurringRead,
    },
  },

  // --------------------------------------------------------- consolidation
  //
  // Spread whole, and declared next to the ledger it reads. §8.16 is the
  // group-level answer to §8.2's single-company statements — the same trial
  // balance, income statement and balance sheet, translated at closing and
  // average rates with eliminations applied — so the two blocks belong beside
  // each other in the file even though the sidebar files consolidation under
  // Finance, next to the FX rates the translation consumes.
  //
  // Five of the nine records are a run's sub-views (`/consol/runs/:id/…`) and
  // none of them appears in the navigation: they are reached from the run, and
  // `RunTabs.vue` is the strip that reaches them.
  ...CONSOL_ROUTES,

  // -------------------------------------------------------------- payables
  //
  // The three document kinds are one screen and one table (`ap_invoices`): a
  // credit note reached from a vendor ledger keeps working whichever list the
  // reader came from. Only the list is kind-scoped; the document route is not,
  // so a link built from a journal's sourceDocId never has to know the kind.
  //
  // `/ap/invoices/new` is declared BEFORE `/ap/invoices/:id(\d+)`. The digit
  // constraint already keeps "new" from matching the id route, but the order is
  // kept explicit because the constraint is easy to drop in a later edit and the
  // failure — a create screen that renders as a document that does not exist —
  // is not obvious from either route on its own.
  {
    path: '/ap/vendors',
    name: 'ap-vendors',
    component: () => import('@/modules/ap/VendorsListView.vue'),
    meta: { titleKey: 'ap.vendors.title', permission: AP_PERMISSIONS.vendorRead },
  },
  {
    path: '/ap/vendors/:id(\\d+)',
    name: 'ap-vendor',
    component: () => import('@/modules/ap/VendorDetailView.vue'),
    meta: { titleKey: 'routes.titles.apVendor', permission: AP_PERMISSIONS.vendorRead },
  },
  {
    path: '/ap/bank-changes',
    name: 'ap-bank-changes',
    component: () => import('@/modules/ap/BankChangesView.vue'),
    meta: { titleKey: 'ap.bankChanges.title', permission: AP_PERMISSIONS.bankRead },
  },
  {
    path: '/ap/invoices',
    name: 'ap-invoices',
    component: () => import('@/modules/ap/ApInvoicesListView.vue'),
    props: { kind: 'invoice' },
    meta: { titleKey: 'routes.titles.apInvoices', permission: AP_PERMISSIONS.invoiceRead },
  },
  {
    path: '/ap/credit-notes',
    name: 'ap-credit-notes',
    component: () => import('@/modules/ap/ApInvoicesListView.vue'),
    props: { kind: 'credit_note' },
    meta: { titleKey: 'routes.titles.apCreditNotes', permission: AP_PERMISSIONS.invoiceRead },
  },
  {
    path: '/ap/debit-notes',
    name: 'ap-debit-notes',
    component: () => import('@/modules/ap/ApInvoicesListView.vue'),
    props: { kind: 'debit_note' },
    meta: { titleKey: 'routes.titles.apDebitNotes', permission: AP_PERMISSIONS.invoiceRead },
  },
  {
    path: '/ap/invoices/new',
    name: 'ap-invoice-new',
    component: () => import('@/modules/ap/ApInvoiceView.vue'),
    meta: { titleKey: 'routes.titles.apInvoiceNew', permission: AP_PERMISSIONS.invoiceCreate },
  },
  {
    path: '/ap/invoices/:id(\\d+)',
    name: 'ap-invoice',
    component: () => import('@/modules/ap/ApInvoiceView.vue'),
    meta: { titleKey: 'routes.titles.apInvoice', permission: AP_PERMISSIONS.invoiceRead },
  },
  {
    path: '/ap/payment-runs',
    name: 'ap-payment-runs',
    component: () => import('@/modules/ap/PaymentRunsListView.vue'),
    meta: { titleKey: 'ap.runs.title', permission: AP_PERMISSIONS.paymentRead },
  },
  {
    path: '/ap/payment-runs/:id(\\d+)',
    name: 'ap-payment-run',
    component: () => import('@/modules/ap/PaymentRunView.vue'),
    meta: { titleKey: 'routes.titles.apPaymentRun', permission: AP_PERMISSIONS.paymentRead },
  },
  {
    path: '/ap/reports/aging',
    name: 'ap-aging',
    component: () => import('@/modules/ap/ApAgingView.vue'),
    meta: { titleKey: 'ap.aging.title', permission: AP_PERMISSIONS.reportRead },
  },

  // ----------------------------------------------------------- receivables
  //
  // AR ships its own RouteRecordRaw[] rather than paths, so it is spread in
  // whole. Its titles live under `ar.routeTitles.*` for the same reason the AP
  // list titles live in `routes.titles`: a module fragment cannot reach the
  // shared namespace, and duplicating the strings would let a screen and its
  // route drift into two names for one page.
  ...AR_ROUTES,
  // FT8-U17 (FSD 2.2.3): Credit Control and the Dunning Effectiveness report.
  ...AR_CREDIT_CONTROL_ROUTES,
  ...AR_DUNNING_EFFECTIVENESS_ROUTES,
  // FT12-U18 (FSD 2.2.3.8–2.2.3.10): the collections worklist, the collection effectiveness
  // index and the unallocated receipts report.
  ...AR_COLLECTIONS_ROUTES,
  ...AR_CEI_ROUTES,
  ...AR_UNALLOCATED_RECEIPTS_ROUTES,
  // FT14-U19 (FSD 2.2.3 V06): write-offs and their approval thresholds.
  ...AR_WRITE_OFF_ROUTES,

  // ------------------------------------------------------------ cash & bank
  {
    path: '/cash/accounts',
    name: 'cash-accounts',
    component: () => import('@/modules/cash/BankAccountsListView.vue'),
    meta: { titleKey: 'cash.accounts.title', permission: CASH_PERMISSIONS.accountRead },
  },
  {
    path: '/cash/accounts/:id(\\d+)',
    name: 'cash-account',
    component: () => import('@/modules/cash/BankAccountView.vue'),
    meta: { titleKey: 'routes.titles.cashAccount', permission: CASH_PERMISSIONS.accountRead },
  },
  // FT4 (R030/R047/R062): the v1 one-step transfers, cash position and live
  // 13-week forecast screens are gone with their server routes. Their old paths
  // are redirects in `legacyRedirects.ts` (transfer instructions, treasury
  // positions, treasury forecast runs) and must not be re-declared here.
  {
    path: '/cash/recurring-items',
    name: 'cash-recurring-items',
    component: () => import('@/modules/cash/RecurringItemsView.vue'),
    meta: { titleKey: 'cash.recurring.title', permission: CASH_PERMISSIONS.recurringRead },
  },

  // ------------------------------------------------------ bank reconciliation
  //
  // Spread whole, like AR: the module ships RouteRecordRaw records carrying their
  // own permissions and title keys, so there is nothing for this file to restate
  // and nothing to drift.
  ...BANKREC_ROUTES,
  // The C4 half of the same module — match sets, the certification screen and the
  // standing match rules. Its fragment was merged into the shared catalogue long
  // ago (`treasury.routeTitles.*` resolves in both locales), but the table itself
  // was never spread, so three built and translated screens had no route at all:
  // `/bankrec/standing-rules` answered the 404 page.
  ...BANKREC_C4_ROUTES,

  // ------------------------------------------------------------------- C4 depth
  //
  // The governed transfer-instruction lifecycle lives inside the cash module
  // (its own document, extended); positions, beneficiaries, mandates, wallets,
  // debt and forecast persistence are the new treasury module. All three ship
  // RouteRecordRaw records carrying their own permissions and title keys.
  ...CASH_TRANSFER_ROUTES,
  ...TREASURY_ROUTES,
  // FT15-U39 (FSD 3.2.1 R020): cash concentration structures.
  ...TREASURY_CONCENTRATION_ROUTES,
  // FT11-U37: the group position (S05) and Liquidity Analysis (R019). `/treasury/positions/group`
  // cannot collide with `/treasury/positions/:id`, which is constrained to digits.
  ...TREASURY_POSITION_GROUP_ROUTES,
  ...TREASURY_LIQUIDITY_ROUTES,
  ...TREASURY_WALLET_EXPENDITURE_ROUTES,
  ...TREASURY_WALLET_COUNTS_ROUTES,
  // FT5: the wallet funding register (TRS R023).
  ...TREASURY_WALLET_FUNDING_ROUTES,
  // FT7: bank connection configuration (TRS-S02).
  ...TREASURY_BANK_CONNECTION_ROUTES,
  // FT8-U38: intercompany instruments (R032) and the compliance screening lists with the
  // exchange-control rules (R055, V085).
  ...TREASURY_INTERCOMPANY_ROUTES,
  ...TREASURY_SCREENING_ROUTES,
  // FT9-U36: the reconciliation exception register and Bank Reconciliation Dashboard (S04, R015).
  ...TREASURY_RECONCILIATION_EXCEPTIONS_ROUTES,
  // FT3: interest accrual runs, the liquidity-gap report, statement import lines,
  // the ECL settings editor, the cost quantity/report screens and the expense
  // card-account and settings screens. Each carries its own permission and title.
  ...TREASURY_ACCRUAL_RUNS_ROUTES,
  ...TREASURY_LIQUIDITY_GAP_ROUTES,
  ...BANKREC_IMPORT_LINES_ROUTES,
  ...AR_ECL_SETTINGS_ROUTES,
  ...COST_QUANTITIES_ROUTES,
  ...COST_REPORT_ROUTES,
  ...EXPENSE_CARD_ACCOUNT_ROUTES,
  ...EXPENSE_SETTINGS_ROUTES,

  // ------------------------------------------------------ budgets and assets
  //
  // Both ship RouteRecordRaw records carrying their own permissions and title
  // keys, so there is nothing for this file to restate and nothing to drift.
  ...BUDGET_ROUTES,
  ...ASSETS_ROUTES,

  // ---------------------------------------------------------------- C3 depth
  //
  // Close is not a purchasable module (server §6: Module "") — a tenant on any
  // plan that can post a journal must be able to close a period — so it carries
  // no module gate here either, the same reasoning tax and pricing already use.
  // The other four ship RouteRecordRaw records carrying their own permissions
  // and title keys, so there is nothing for this file to restate.
  ...CLOSE_ROUTES,
  ...CLOSE_YEAR_END_ROUTES,
  // FT13-U28 (FSD 2.2.8): the certification register, sub-ledger to GL reconciliation, evidence
  // packs, cycle analysis and the financial statement sets (list and detail).
  ...CLOSE_CERTIFICATIONS_ROUTES,
  ...CLOSE_RECONCILIATION_ROUTES,
  ...CLOSE_EVIDENCE_PACKS_ROUTES,
  ...CLOSE_CYCLE_ANALYSIS_ROUTES,
  ...CLOSE_STATEMENTS_ROUTES,
  ...COST_ROUTES,
  ...GL_GOVERNANCE_ROUTES,
  ...AP_C3_ROUTES,
  ...AP_SETTINGS_ROUTES,
  // FT10-U16: the four AP report screens (FSD 2.2.2, FIN-F02).
  ...AP_SUPPLIER_STATEMENT_ROUTES,
  ...AP_STATEMENT_RECONCILIATION_ROUTES,
  ...AP_WITHHOLDING_ROUTES,
  ...AP_DUPLICATE_PAYMENTS_ROUTES,
  ...INTERCO_ROUTES,
  ...EXPENSE_ROUTES,

  // ------------------------------------------------ inventory and procurement
  ...INVENTORY_ROUTES,
  ...PROCUREMENT_ROUTES,

  // ----------------------------------------------------------- manufacturing
  //
  // Declared after the two modules it consumes: a production order issues the
  // stock inventory holds and is planned against the purchase orders
  // procurement raises (§8.15). Spread whole — the module owns its paths, its
  // permissions and its title keys.
  ...MFG_ROUTES,

  // -------------------------------------------------------------------- qms
  //
  // C7 (docs/design/C7_PROCUREMENT_AND_MFG.md §5.0): a NEW module, callable
  // from goods receipts (above), production (above) and shipments (below) —
  // declared after all three so its own screens read as "quality sits beside
  // the documents it inspects", not before them.
  ...QMS_ROUTES,

  // ------------------------------------------------------------ sales and CRM
  ...SALES_ROUTES,
  ...CRM_ROUTES,
  // Accounts, contacts, segments, campaigns, forecast depth and lead scoring.
  //
  // The module's own note explains that this table waits to be spread until its
  // locale fragment is merged, because `nav/keys.test.ts` walks the router. The
  // fragment is merged — every `crm.c5.*` key resolves in `en.json` and `dv.json`
  // — but the spread never followed, and the sidebar meanwhile gained `Accounts`
  // and `Campaigns` entries pointing at paths no route matched. Two live dead
  // links, and seven further screens (contacts, catalogues, segments, forecast,
  // win/loss, scoring, routing) with nowhere to be reached from.
  ...CRM_C5_ROUTES,

  // ------------------------------------------------------------ C5 party spine
  //
  // Identity, consent, DSR (P4 not yet built — see party module's own routes.ts
  // for what is reachable today), retention, merge, loyalty and the 360. No
  // module gate (server §6, Module ""): a tenant on any plan must be able to
  // answer a data-subject request.
  ...PARTY_ROUTES,

  // ------------------------------------------------------------------ pricing
  //
  // C2 (docs/design/C2_PRICING_AND_SALES.md §5.0, §5.7): the pricing authority
  // extracted out of `internal/sales`/`SALES_ROUTES` into its own engine and its
  // own module, reachable independently of the sales module's plan entitlement
  // (§1.3 — a POS or PMS surface must still be able to price without "sales").
  ...PRICING_ROUTES,
  // Four bookmarks a user may still hold from before the extraction. The server
  // routes they called no longer exist (`sales/routes.ts`'s own doc: "leaving the
  // old paths mounted would leave four screens whose every request 404s"), so
  // these are router-only redirects — no server route, no permission check of
  // their own, they just hand off to the screen that replaced them.
  { path: '/sales/price-lists', redirect: '/pricing/price-lists' },
  { path: '/sales/price-lists/new', redirect: '/pricing/price-lists/new' },
  { path: '/sales/price-lists/:id(\\d+)', redirect: (to) => `/pricing/price-lists/${to.params.id}` },
  { path: '/sales/promotions', redirect: '/pricing/promotions' },

  // ------------------------------------------------------- people and payroll
  //
  // HR owns the employee record; payroll reads it over the API and never its
  // tables. Two route tables for the same reason: two modules, two permission
  // families, one part of the sidebar.
  ...HR_ROUTES,
  ...PAYROLL_ROUTES,

  // C10 (docs/design/C10_WORKFORCE.md §5.0, §5.8, §13 P11): the person/assignment
  // split, work permits, leave depth and exits (HR); rostering, working time and
  // demand (a NEW module, roster/); wage bases, statutory rules, retro,
  // reconciliation, service charge and returns (payroll). Three tables because
  // §5.3 makes rostering its own package — folding it into HR_C10_ROUTES would
  // make HR the owner of a module boundary the design deliberately drew
  // elsewhere.
  ...HR_C10_ROUTES,
  ...ROSTER_ROUTES,
  ...PAYROLL_C10_ROUTES,

  // ---------------------------------------------------- employee self-service
  //
  // The same subject matter as the two tables above, seen from the other side:
  // `/hr/employees/7` is a manager reading somebody's record, `/me` is a person
  // reading their own. Two route families rather than one screen with a flag,
  // because the API is two families — every `/api/v1/me/...` endpoint resolves
  // the employee from the JWT and can never be pointed at a colleague.
  //
  // `/me` is not `/profile`. `/profile` is the account: password, MFA, sessions.
  // `/me` is the employment: contract, payslips, leave, attendance, HR file.
  ...ESS_ROUTES,
  // C10 ESS additions: own roster, clock, service-charge allocation, employment
  // history and year-end statements (§5.8, §11).
  ...ESS_C10_ROUTES,

  // ----------------------------------------------------- projects and service
  //
  // `/projects/timesheets` is declared inside PROJECTS_ROUTES before
  // `/projects/:id(\d+)`, so the spread order here is the module's, not this
  // file's, and must stay a whole-table spread.
  ...PROJECTS_ROUTES,
  ...SERVICE_ROUTES,
  // C11 (docs/design/C11_PROJECTS_SERVICE_EAM.md §5.1, §13 P15): the EAM register,
  // maintenance and compliance screens (P13). C11's projects screens (P14) were
  // never dispatched this cycle — see this packet's own report — so PROJECTS_ROUTES
  // above is still M10's.
  ...EAM_ROUTES,
  // C0 tax spine: jurisdictions, registrations, certificates, returns,
  // pack activation and the determination waterfall. The v1 /tax/codes and
  // /tax/report routes above stay live; C1 retires them with the adapters.
  ...TAX_SPINE_ROUTES,

  // C8 (docs/design/C8_POS_AND_OFFLINE.md §5.5, §13 P15): the back office for
  // the point-of-sale module and the offline kit's device fleet — outlets,
  // terminals, devices, content sets, menus, sales, cash sessions, the
  // day-close board, the anomaly board, sync conflicts and ratifications. The
  // till itself is the separate `pos/` SPA (§5.7); this table is the tenant
  // app's screens onto the same data.
  ...POS_ROUTES,

  // C9 (docs/design/C9_PMS.md §5.5, §13 P15): property management's back
  // office, three tables (commercial half P12, housekeeping P13, operations
  // half P14) because §13 splits the module across three packets and folding
  // them into one table would make whichever landed second the owner of the
  // other's paths. The housekeeping FIELD tablet is the separate `field/` SPA
  // (§5.7); these tables are the tenant app's screens onto the same data.
  ...PMS_ROUTES,
  ...PMS_HOUSEKEEPING_ROUTES,
  ...PMS_OPS_ROUTES,

  // C12 (docs/design/C12_RVM_REPORTING_AI.md §5.5, §13 P9/P10/P11): the metric
  // dictionary, certifications, the daily operating report/board and role
  // exception views (P9); the AI Assistant's non-chat screens — briefings,
  // autonomy, the recommendation ledger, config anomalies (P9); and revenue
  // management — on the books, pace, forecasts and their variance control,
  // accuracy, displacement, hurdles, competitor rates and the daily commercial
  // report (P10). RVM was wired after the rest of C12: its packet ran once the
  // contract carried its 62 routes, which is why it is a separate line here.
  ...REPORTING_ROUTES,
  ...AI_ROUTES,
  ...RVM_ROUTES,

  {
    path: '/users',
    name: 'users',
    component: () => import('@/modules/users/UsersListView.vue'),
    meta: { titleKey: 'routes.titles.users', permission: PERMISSIONS.userList },
  },
  {
    path: '/roles',
    name: 'roles',
    component: () => import('@/modules/roles/RolesView.vue'),
    meta: { titleKey: 'routes.titles.roles', permission: PERMISSIONS.roleList },
  },
  {
    path: '/tax/codes',
    name: 'tax-codes',
    component: () => import('@/modules/tax/TaxCodesView.vue'),
    meta: { titleKey: 'routes.titles.taxCodes', permission: PERMISSIONS.taxCodeRead },
  },
  {
    path: '/tax/report',
    name: 'tax-report',
    component: () => import('@/modules/tax/TaxReportView.vue'),
    meta: { titleKey: 'routes.titles.taxReport', permission: PERMISSIONS.taxReportRead },
  },
  {
    path: '/fx/currencies',
    name: 'currencies',
    component: () => import('@/modules/fx/CurrenciesView.vue'),
    meta: { titleKey: 'routes.titles.currencies', permission: PERMISSIONS.fxCurrencyRead },
  },
  // C1 FX rate types and tolerance holds (design §5.9): one route, the held-rate
  // decision queue. Spread rather than inlined here for the same reason every
  // other module's own table is spread whole — see fx/routesC1.ts.
  ...FX_C1_ROUTES,
  {
    path: '/audit',
    name: 'audit',
    component: () => import('@/modules/audit/AuditExplorerView.vue'),
    meta: { titleKey: 'routes.titles.audit', permission: PERMISSIONS.auditRead },
  },
  {
    path: '/profile',
    name: 'profile',
    component: () => import('@/modules/profile/ProfileView.vue'),
    meta: { titleKey: 'routes.titles.profile' },
  },

  {
    path: '/forbidden',
    name: 'forbidden',
    component: () => import('@/modules/errors/ForbiddenView.vue'),
    meta: { titleKey: 'routes.titles.forbidden' },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/modules/errors/NotFoundView.vue'),
    meta: { titleKey: 'routes.titles.notFound' },
  },
]

/**
 * Move an open page to the sign-in screen when its session ends without
 * anyone navigating — a refresh that failed (`sessionExpired`, raised by the
 * transport). The auth store tears the session down synchronously in the same
 * assignment, and `App.vue` unmounts the shell on the next render; this is
 * what leaves the tab somewhere a person can act, instead of an empty surface
 * on a URL that needs a session.
 *
 * Exported (and called by `createAppRouter`) so a test can install exactly
 * this behaviour on a router of its own.
 *
 * Three cases where it deliberately does nothing:
 *  - a public route (`/login` itself, forgot/reset password, invite
 *    acceptance): there is nothing to leave, and replacing `/login` with
 *    `/login?redirect=/login` would be a loop;
 *  - before the first navigation has resolved (`START_LOCATION`): the cold-
 *    load guard is mid-bootstrap and is about to send the user to `/login`
 *    itself — with the URL they actually asked for as `redirect`. Replacing
 *    here would cancel that navigation and remember `/` instead;
 *  - the flag going back down (a new sign-in clears it).
 *
 * A bare, non-public route (the forced password change) gets no `redirect`:
 * its own URL is not a destination anyone should be returned to.
 */
export function followSessionEnd(router: Router): void {
  watch(sessionExpired, (expired) => {
    if (!expired) return
    const current = router.currentRoute.value
    if (current === START_LOCATION) return
    if (current.meta.public === true) return
    if (current.meta.bare === true) {
      void router.replace({ name: 'login' })
      return
    }
    void router.replace({ name: 'login', query: { redirect: current.fullPath } })
  })
}

export function createAppRouter(): ReturnType<typeof createRouter> {
  const router = createRouter({
    history: createWebHistory(),
    routes,
    scrollBehavior: (_to, _from, saved) => saved ?? { top: 0 },
  })

  router.beforeEach(async (to) => {
    const auth = useAuthStore()

    // Restore the session before deciding anything, or a hard refresh on a
    // protected URL would bounce a perfectly valid session to the login screen.
    if (!auth.bootstrapped) await auth.bootstrap()

    if (to.meta.public === true) {
      if (auth.isAuthenticated && (to.name === 'login' || to.name === 'forgot-password')) {
        return { name: 'dashboard' }
      }
      return true
    }

    if (!auth.isAuthenticated) {
      return { name: 'login', query: { redirect: to.fullPath } }
    }

    // A password somebody else chose is a session that can do exactly one
    // thing. The server enforces it (every tenant route outside `auth.*`
    // answers 403 ERR_PASSWORD_CHANGE_REQUIRED); this is what stops the user
    // meeting that refusal as a broken screen instead of as an instruction.
    //
    // Two sources, because there are two ways to learn it: `mustChangePassword`
    // rides on `/auth/me`, and the transport raises `passwordChangeRequired`
    // when a request from an already-open page comes back 403.
    const blocked = auth.mustChangePassword || passwordChangeRequired.value
    if (blocked) {
      // `bare` is the escape hatch, and `change-password` is the only non-public
      // bare route — so this admits the screen that lifts the block and nothing
      // else. Signing out stays reachable because `/login` is public and
      // returned above.
      if (to.meta.bare === true) return true
      return { name: 'change-password', query: { redirect: to.fullPath } }
    }

    // Nobody arrives here by choice. A user who is not blocked and asks for the
    // screen anyway wants the password panel in their profile.
    if (to.name === 'change-password') return { name: 'profile' }

    // XFN-UX-013 (role landing) deliberately does NOT redirect here any more.
    // `/` is the home screen — the launcher every app and shared screen hangs
    // off — and bouncing a mapped role away from it would hide all of that
    // from them. The role's dashboard is instead the first card on the home
    // screen; see `roleLanding.ts`'s header for the reasoning.

    const required = to.meta.permission
    if (required !== undefined && !auth.can(required)) {
      return { name: 'forbidden', query: { from: to.fullPath } }
    }

    return true
  })

  // The backstop for a page that was already open. The guard above covers
  // navigation, but a screen mounted before the block began learns about it from
  // a 403 on its own request, and nothing would move it. Watching the transport's
  // flag here — rather than pushing from `api/client.ts` — keeps the import
  // arrows one-way (router -> client), exactly as `api/stepup.ts` does.
  watch(passwordChangeRequired, (required) => {
    if (!required) return
    const current = router.currentRoute.value
    if (current.name === 'change-password') return
    void router.push({ name: 'change-password', query: { redirect: current.fullPath } })
  })

  followSessionEnd(router)

  router.afterEach((to) => {
    const key = to.meta.titleKey
    document.title =
      key === undefined ? t('app.name') : t('routes.documentTitle', { title: t(key) })
  })

  return router
}
